import { Service } from './service';
import { ITokenManager } from '../token-manager/token-manager.types';
import { V2Router } from '../router/v2.router';
import { LoggerNames } from '../lib/constants';
import { Logger } from '../lib/logger';
import { isBrowser } from '../lib/browser';

export interface SingleSignoutServiceOptions {
  singleSignout?: boolean;
  syncStorage?: boolean;
}

export class SingleSignoutService implements Service {
  // 5 minute timeout
  public static readonly CHECK_SESSION_TIMEOUT = 1000 * 60 * 5;

  private readonly tokenManager: ITokenManager;
  private readonly router: V2Router;
  private readonly logger = Logger.createLogger(LoggerNames.SINGLE_SIGN_OUT);

  // zero is a safe default timeout value
  // setTimeout returns non-zero integer, so this will never conflict,
  // and clearTimeout does nothing if zero is passed
  // https://developer.mozilla.org/en-US/docs/Web/API/setTimeout
  // https://developer.mozilla.org/en-US/docs/Web/API/clearTimeout
  private checkSessionTimeout: number = 0;
  private started: boolean = false;
  private options: SingleSignoutServiceOptions;

  constructor(tokenManager: ITokenManager, router: V2Router, options: SingleSignoutServiceOptions) {
    this.tokenManager = tokenManager;
    this.router = router;
    this.options = options;

    this.checkSession = this.checkSession.bind(this);
    this.createCheckSessionTimeout = this.createCheckSessionTimeout.bind(this);
  }

  canStart(): Boolean {
    return !this.started && Boolean(this.options.singleSignout);
  }

  isStarted(): Boolean {
    return this.started;
  }

  requiresLeadership(): boolean {
    return Boolean(this.options.syncStorage) && isBrowser();
  }

  /**
   * Starts the service. If the user currently has tokens in storage, this will create a 5 minute timeout
   * to revalidate the session is still active. If the session becomes inactive, the tokens will be cleared.
   */
  async start(): Promise<void> {
    if (!this.canStart()) {
      this.logger.warn(`Single sign out cannot be started or is already running`);
      return;
    }

    this.logger.debug('Starting single sign out service');
    await this.checkSession();
    // set up a listener to check the session when a new token is added
    this.tokenManager.on('added', this.createCheckSessionTimeout);
    this.started = true;
    return;
  }

  async stop(): Promise<void> {
    this.tokenManager.off('added', this.createCheckSessionTimeout);
    clearTimeout(this.checkSessionTimeout);
    this.started = false;
    return;
  }

  private async createCheckSessionTimeout() {
    // Clear the timeout, so we don't have multiple timeouts running
    clearTimeout(this.checkSessionTimeout);
    const nextSessionCheck = Date.now() + SingleSignoutService.CHECK_SESSION_TIMEOUT;
    this.logger.debug(
      'Creating new check session timeout. Check will run at ',
      new Date(nextSessionCheck).toISOString(),
    );
    this.checkSessionTimeout = setTimeout(this.checkSession, SingleSignoutService.CHECK_SESSION_TIMEOUT);
  }

  private async checkSession(): Promise<boolean> {
    // if the user doesn't have an access token or it is expired, we can skip checking the session
    const accessToken = this.tokenManager.accessToken;
    if (!accessToken || this.tokenManager.hasExpired(accessToken)) return false;

    const hasSession = await this.router.checkSession({ accessToken: accessToken.token });

    // if the user has a session, we want to check again in 5 minutes
    if (hasSession) {
      this.logger.debug('User session is still active');
      await this.createCheckSessionTimeout();
      return true;
    }

    // otherwise clear the tokens and don't check again until user becomes authenticated
    this.logger.info('User session has expired, clearing tokens');
    this.tokenManager.clear();
    this.checkSessionTimeout = 0;
    return false;
  }
}
